import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Badge, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const ReportesValores = () => {
  const [socios, setSocios] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAño, setFilterAño] = useState(new Date().getFullYear());
  const [selectedSocio, setSelectedSocio] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchSocios();
    fetchRegistros();
  }, [filterAño]);

  const fetchSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
    } catch (error) {
      console.error('Error fetching socios:', error);
      toast.error('Error al cargar socios');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistros = async () => {
    try {
      const response = await axios.get(`/api/registros-mensuales?año=${filterAño}`);
      setRegistros(response.data.data || []);
    } catch (error) {
      console.error('Error fetching registros:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getNombreMes = (mes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes - 1];
  };

  const obtenerRegistroMes = (socioId, mes) => {
    return registros.find(r => r.socio._id === socioId && r.mes === mes);
  };

  const calcularTotalesSocio = (socioId) => {
    const registrosSocio = registros.filter(r => r.socio._id === socioId);
    return {
      totalAportes: registrosSocio.reduce((sum, r) => sum + (r.aportes || 0), 0),
      totalCapital: registrosSocio.reduce((sum, r) => sum + (r.capitalPrestamo || 0), 0),
      totalInteres: registrosSocio.reduce((sum, r) => sum + (r.interesPrestamo || 0), 0),
      totalCuotaPrestamo: registrosSocio.reduce((sum, r) => sum + ((r.capitalPrestamo || 0) + (r.interesPrestamo || 0)), 0),
      totalPaseo: registrosSocio.reduce((sum, r) => sum + (r.paseo || 0), 0),
      mesesRegistrados: registrosSocio.length
    };
  };

  const handleVerDetalles = (socio) => {
    setSelectedSocio(socio);
    setShowDetailModal(true);
  };

  const handleExportarPDF = () => {
    toast.info('Función de exportar PDF en desarrollo');
  };

  const handleExportarExcel = () => {
    toast.info('Función de exportar Excel en desarrollo');
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2 className="text-gradient mb-2">
                      <i className="fas fa-chart-bar me-2"></i>
                      Reportes de Valores Mensuales
                    </h2>
                    <p className="text-muted mb-0">
                      Visualiza los aportes, cuotas de préstamo y paseos mes a mes
                    </p>
                  </div>
                  <div>
                    <Button variant="outline-primary" size="sm" onClick={handleExportarPDF} className="me-2">
                      <i className="fas fa-file-pdf me-1"></i>
                      PDF
                    </Button>
                    <Button variant="outline-success" size="sm" onClick={handleExportarExcel}>
                      <i className="fas fa-file-excel me-1"></i>
                      Excel
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtro de Año */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="card-custom">
              <Card.Body>
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-2"></i>
                    Año
                  </Form.Label>
                  <Form.Select value={filterAño} onChange={(e) => setFilterAño(parseInt(e.target.value))}>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Resumen Anual */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-table me-2"></i>
                  Resumen Anual {filterAño}
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Socio</th>
                        <th>Cédula</th>
                        <th>Número</th>
                        <th className="text-end">Total Aportes</th>
                        <th className="text-end">Total Capital</th>
                        <th className="text-end">Total Interés</th>
                        <th className="text-end bg-light">Total Cuota Préstamo</th>
                        <th className="text-end">Total Paseo</th>
                        <th className="text-end">Total General</th>
                        <th className="text-center">Meses</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                            No hay socios registrados
                          </td>
                        </tr>
                      ) : (
                        socios.map(socio => {
                          const totales = calcularTotalesSocio(socio._id);
                          const totalGeneral = totales.totalAportes + totales.totalCapital + totales.totalInteres + totales.totalPaseo;
                          
                          return (
                            <tr key={socio._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                                  <div>
                                    <div className="fw-bold">{socio.name}</div>
                                    <small className="text-muted">{socio.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>{socio.cedula}</td>
                              <td>
                                <Badge bg="info">{socio.socioInfo?.numeroSocio || 'N/A'}</Badge>
                              </td>
                              <td className="text-end">
                                <strong className="text-success">
                                  {formatCurrency(totales.totalAportes)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-primary">
                                  {formatCurrency(totales.totalCapital)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-danger">
                                  {formatCurrency(totales.totalInteres)}
                                </strong>
                              </td>
                              <td className="text-end bg-light">
                                <strong className="text-info">
                                  {formatCurrency(totales.totalCuotaPrestamo)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-warning">
                                  {formatCurrency(totales.totalPaseo)}
                                </strong>
                              </td>
                              <td className="text-end">
                                <strong className="text-info">
                                  {formatCurrency(totalGeneral)}
                                </strong>
                              </td>
                              <td className="text-center">
                                <Badge bg="secondary">{totales.mesesRegistrados}</Badge>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleVerDetalles(socio)}
                                  title="Ver detalles mensuales"
                                >
                                  <i className="fas fa-eye me-1"></i>
                                  Detalles
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Modal de Detalles Mensuales */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-alt me-2"></i>
            Detalles Mensuales - {selectedSocio?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSocio && (
            <DetallesMensualesTable 
              socio={selectedSocio}
              registros={registros}
              año={filterAño}
              formatCurrency={formatCurrency}
              getNombreMes={getNombreMes}
              obtenerRegistroMes={obtenerRegistroMes}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            <i className="fas fa-times me-2"></i>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Componente para mostrar detalles mensuales
const DetallesMensualesTable = ({ socio, registros, año, formatCurrency, getNombreMes, obtenerRegistroMes }) => {
  const registrosSocio = registros.filter(r => r.socio._id === socio._id);
  
  const totales = {
    aportes: 0,
    capital: 0,
    interes: 0,
    paseo: 0
  };

  return (
    <div className="table-responsive">
      <Table striped bordered hover>
        <thead className="table-light">
          <tr>
            <th className="text-center">Mes</th>
            <th className="text-end">Aportes</th>
            <th className="text-end">Capital</th>
            <th className="text-end">Interés</th>
            <th className="text-end bg-light">Cuota Préstamo</th>
            <th className="text-end">Paseo</th>
            <th className="text-end">Total Mes</th>
            <th className="text-center">Estado</th>
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(mes => {
            const registro = obtenerRegistroMes(socio._id, mes);
            const aportes = registro?.aportes || 0;
            const capital = registro?.capitalPrestamo || 0;
            const interes = registro?.interesPrestamo || 0;
            const cuotaPrestamo = capital + interes;
            const paseo = registro?.paseo || 0;
            const totalMes = aportes + capital + interes + paseo;

            totales.aportes += aportes;
            totales.capital += capital;
            totales.interes += interes;
            totales.paseo += paseo;

            return (
              <tr key={mes}>
                <td className="text-center fw-bold">{getNombreMes(mes)}</td>
                <td className="text-end">
                  <span className={aportes > 0 ? 'text-success fw-bold' : 'text-muted'}>
                    {formatCurrency(aportes)}
                  </span>
                </td>
                <td className="text-end">
                  <span className={capital > 0 ? 'text-primary fw-bold' : 'text-muted'}>
                    {formatCurrency(capital)}
                  </span>
                </td>
                <td className="text-end">
                  <span className={interes > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                    {formatCurrency(interes)}
                  </span>
                </td>
                <td className="text-end bg-light">
                  <span className={cuotaPrestamo > 0 ? 'text-info fw-bold' : 'text-muted'}>
                    {formatCurrency(cuotaPrestamo)}
                  </span>
                </td>
                <td className="text-end">
                  <span className={paseo > 0 ? 'text-warning fw-bold' : 'text-muted'}>
                    {formatCurrency(paseo)}
                  </span>
                </td>
                <td className="text-end">
                  <strong className={totalMes > 0 ? 'text-info' : 'text-muted'}>
                    {formatCurrency(totalMes)}
                  </strong>
                </td>
                <td className="text-center">
                  {registro ? (
                    <Badge bg="success">
                      <i className="fas fa-check me-1"></i>
                      Registrado
                    </Badge>
                  ) : (
                    <Badge bg="warning">
                      <i className="fas fa-exclamation me-1"></i>
                      Sin Registro
                    </Badge>
                  )}
                </td>
              </tr>
            );
          })}
          <tr className="table-light fw-bold">
            <td className="text-center">TOTAL</td>
            <td className="text-end text-success">{formatCurrency(totales.aportes)}</td>
            <td className="text-end text-primary">{formatCurrency(totales.capital)}</td>
            <td className="text-end text-danger">{formatCurrency(totales.interes)}</td>
            <td className="text-end bg-light text-info">{formatCurrency(totales.capital + totales.interes)}</td>
            <td className="text-end text-warning">{formatCurrency(totales.paseo)}</td>
            <td className="text-end text-info">
              {formatCurrency(totales.aportes + totales.capital + totales.interes + totales.paseo)}
            </td>
            <td className="text-center">-</td>
          </tr>
        </tbody>
      </Table>

      {/* Resumen */}
      <Row className="mt-4">
        <Col md={3}>
          <Card className="bg-light border-success">
            <Card.Body className="p-3">
              <small className="text-muted d-block mb-2">
                <i className="fas fa-hand-holding-usd text-success me-1"></i>
                Total Aportes
              </small>
              <h5 className="text-success mb-0">{formatCurrency(totales.aportes)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-light border-primary">
            <Card.Body className="p-3">
              <small className="text-muted d-block mb-2">
                <i className="fas fa-coins text-primary me-1"></i>
                Total Capital
              </small>
              <h5 className="text-primary mb-0">{formatCurrency(totales.capital)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-light border-danger">
            <Card.Body className="p-3">
              <small className="text-muted d-block mb-2">
                <i className="fas fa-percent text-danger me-1"></i>
                Total Interés
              </small>
              <h5 className="text-danger mb-0">{formatCurrency(totales.interes)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="bg-light border-info">
            <Card.Body className="p-3">
              <small className="text-muted d-block mb-2">
                <i className="fas fa-credit-card text-info me-1"></i>
                Total Cuota Préstamo
              </small>
              <h5 className="text-info mb-0">{formatCurrency(totales.capital + totales.interes)}</h5>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={3}>
          <Card className="bg-light border-warning">
            <Card.Body className="p-3">
              <small className="text-muted d-block mb-2">
                <i className="fas fa-bus text-warning me-1"></i>
                Total Paseo
              </small>
              <h5 className="text-warning mb-0">{formatCurrency(totales.paseo)}</h5>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReportesValores;
